import { useEffect, useMemo, useRef, useState } from 'react';
import {
  bookableOptions, computeTotal, nightsBetween, formatMoney, todayISO, addDaysISO,
  RESERVATION_SOURCES, propertyTypeMeta,
  type HebergementProperty, type HebergementUnit,
} from '@/lib/hebergement';
import type { ReservationDraft } from '@/hooks/useHebergement';
import { newIdempotencyKey } from '@/lib/coreActionBridge';

export interface ReservationPrefill {
  propertyId?: number;
  unitId?: number | null;
  checkIn?: string;
  checkOut?: string;
  pricePerNight?: number;
}

interface Props {
  properties: HebergementProperty[];
  units: HebergementUnit[];
  currency: string;
  prefill?: ReservationPrefill | null;
  checkAvailability: (propertyId: number, unitId: number | null, checkIn: string, checkOut: string) => Promise<boolean>;
  createReservation: (draft: ReservationDraft, opts?: { idempotencyKey?: string | null }) => Promise<number>;
  onClose: () => void;
  onCreated: () => void;
}

export default function ReservationFormModal({
  properties, units, currency, prefill, checkAvailability, createReservation, onClose, onCreated,
}: Props) {
  const [propertyId, setPropertyId] = useState<number | null>(prefill?.propertyId ?? properties[0]?.id ?? null);
  const [optionValue, setOptionValue] = useState<string>('property');
  const [checkIn, setCheckIn] = useState(prefill?.checkIn || todayISO());
  const [checkOut, setCheckOut] = useState(prefill?.checkOut || addDaysISO(todayISO(), 1));
  const [guests, setGuests] = useState('2');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [pricePerNight, setPricePerNight] = useState(String(prefill?.pricePerNight ?? 0));
  const [discount, setDiscount] = useState('0');
  const [fees, setFees] = useState('0');
  const [source, setSource] = useState('manual');
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState<'unknown' | 'checking' | 'available' | 'unavailable'>('unknown');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Clé d'idempotence mémorisée le temps d'un envoi (retry-safe, spec 47).
  const idemKeyRef = useRef<string>('');

  const property = properties.find((p) => p.id === propertyId) || null;
  const options = useMemo(
    () => (property ? bookableOptions(property, units.filter((u) => u.property_id === property.id)) : []),
    [property, units],
  );

  // Aligne l'option + le prix quand le bien change
  useEffect(() => {
    if (!property) return;
    const opts = bookableOptions(property, units.filter((u) => u.property_id === property.id));
    const match = prefill?.unitId != null ? opts.find((o) => o.unitId === prefill.unitId) : opts[0];
    const chosen = match || opts[0];
    if (chosen) {
      setOptionValue(chosen.value);
      setPricePerNight(String(chosen.price));
    }
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOption = options.find((o) => o.value === optionValue) || options[0] || null;
  const unitId = selectedOption?.unitId ?? null;

  const onOptionChange = (value: string) => {
    setOptionValue(value);
    const opt = options.find((o) => o.value === value);
    if (opt) setPricePerNight(String(opt.price));
  };

  const nights = nightsBetween(checkIn, checkOut);
  const total = computeTotal(parseFloat(pricePerNight) || 0, checkIn, checkOut, parseFloat(discount) || 0, parseFloat(fees) || 0);

  const runCheck = async () => {
    if (!propertyId || !checkIn || !checkOut || checkOut <= checkIn) { setAvailability('unknown'); return; }
    setAvailability('checking');
    const ok = await checkAvailability(propertyId, unitId, checkIn, checkOut);
    setAvailability(ok ? 'available' : 'unavailable');
  };

  useEffect(() => {
    const timer = setTimeout(() => { runCheck(); }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, unitId, checkIn, checkOut]);

  const submit = async () => {
    setError('');
    if (!propertyId) { setError('Sélectionnez un bien.'); return; }
    if (!checkIn || !checkOut || checkOut <= checkIn) { setError('Dates invalides : le départ doit suivre l\u2019arrivée.'); return; }
    if (!guestName.trim()) { setError('Le nom du client est obligatoire.'); return; }
    setSaving(true);
    try {
      // Idempotence (47) : un double clic / rejeu réseau ne crée pas 2 réservations.
      if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey('resa');
      await createReservation(
        {
          propertyId,
          unitId,
          guestName: guestName.trim(),
          guestEmail: guestEmail.trim(),
          guestPhone: guestPhone.trim(),
          checkIn,
          checkOut,
          guests: Math.max(1, parseInt(guests, 10) || 1),
          pricePerNight: parseFloat(pricePerNight) || 0,
          discount: parseFloat(discount) || 0,
          fees: parseFloat(fees) || 0,
          source,
          notes: notes.trim(),
        },
        { idempotencyKey: idemKeyRef.current },
      );
      idemKeyRef.current = '';
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réservation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-2xl bg-background-50 sm:rounded-2xl rounded-t-2xl border border-background-200/70 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground-950">Nouvelle réservation</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
              <span className="text-xs text-red-700">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Bien *</label>
              <select value={propertyId ?? ''} onChange={(e) => setPropertyId(Number(e.target.value))}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300 cursor-pointer">
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Unité</label>
              <select value={optionValue} onChange={(e) => onOptionChange(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300 cursor-pointer">
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Arrivée *</label>
              <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Départ *</label>
              <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
          </div>

          {/* Statut dispo */}
          <div className={`px-3 py-2.5 rounded-md flex items-center gap-2 text-xs ${
            availability === 'available' ? 'bg-accent-50 text-accent-700'
            : availability === 'unavailable' ? 'bg-red-50 text-red-600'
            : 'bg-background-100 text-foreground-500'
          }`}>
            <i className={availability === 'checking' ? 'ri-loader-4-line animate-spin' : availability === 'available' ? 'ri-checkbox-circle-line' : availability === 'unavailable' ? 'ri-close-circle-line' : 'ri-information-line'}></i>
            {availability === 'checking' ? 'Vérification de la disponibilité…'
              : availability === 'available' ? 'Disponible sur ces dates.'
              : availability === 'unavailable' ? 'Indisponible sur ces dates (réservation ou blocage existant).'
              : 'Sélectionnez les dates.'}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom du client *</label>
              <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Ex. Youssef B."
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Voyageurs</label>
              <input type="number" min={1} value={guests} onChange={(e) => setGuests(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Email</label>
              <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="client@email.com"
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Téléphone</label>
              <input type="text" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+212…"
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Source</label>
              <select value={source} onChange={(e) => setSource(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300 cursor-pointer">
                {RESERVATION_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Prix / nuit</label>
              <input type="number" min={0} value={pricePerNight} onChange={(e) => setPricePerNight(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Remise</label>
              <input type="number" min={0} value={discount} onChange={(e) => setDiscount(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Frais</label>
              <input type="number" min={0} value={fees} onChange={(e) => setFees(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Notes internes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500}
              className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm resize-none focus:outline-none focus:border-primary-300" />
          </div>

          {/* Récapitulatif */}
          <div className="px-4 py-3 rounded-lg bg-background-100 flex items-center justify-between">
            <div className="text-xs text-foreground-600">
              {nights > 0 ? `${nights} nuit${nights > 1 ? 's' : ''}` : '—'}
              {property && <span className="ml-1 text-foreground-400">· {propertyTypeMeta(property.type).label}</span>}
            </div>
            <div className="text-base font-bold font-heading text-foreground-950">{formatMoney(total, currency)}</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 h-16 border-t border-background-200/70 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
          <button onClick={submit} disabled={saving || availability === 'unavailable'}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
            {saving ? <><i className="ri-loader-4-line animate-spin"></i>Réservation…</> : <><i className="ri-calendar-check-line"></i>Confirmer</>}
          </button>
        </div>
      </div>
    </div>
  );
}